// Tenant request detail page. Three personas share this surface:
//   - REQUESTER (and equivalent property/contract managers): add/remove items,
//     send the draft, review uploaded items, cancel the request.
//   - TENANT: upload files against PENDING / REJECTED items.
//   - OBSERVER: read-only.
// The backend already routes on viewer; we use the embedded `viewer` field
// to decide which controls to render and never try to second-guess access.

import { Component, esc } from '../core/component.js';
import { api, ApiError } from '../core/api.js';
import { formatDate, formatDateTime } from '../core/format.js';
import { icon } from '../core/icons.js';
import { t } from '../core/i18n.js';

const ITEM_KINDS = [
  'ID_CARD_FRONT', 'ID_CARD_BACK', 'PASSPORT', 'ADDRESS_CARD',
  'PROOF_OF_INCOME', 'EMPLOYMENT_LETTER', 'TAX_CERTIFICATE',
  'BANK_STATEMENT', 'PHOTO', 'OTHER',
];

export class TenantRequestView extends Component {
  constructor({ params }) {
    super();
    this.id = params.id;
    this.state = {
      request: null,
      error: null,
      addItemOpen: false,
      busy: false,
      rejectOpenItemId: null,
      rejectReason: '',
    };
  }

  template() {
    const r = this.state.request;
    if (!r && !this.state.error) return `<div class="muted">${esc(t('common.loading'))}</div>`;
    if (this.state.error && !r) return `<div class="error-banner">${esc(this.state.error)}</div>`;
    const viewer = r.viewer || 'OBSERVER';
    const isReq = viewer === 'REQUESTER';
    const isTenant = viewer === 'TENANT';
    return `
      <section class="stack">
        <a href="#/tenant-requests/${isTenant ? 'incoming' : 'outgoing'}" class="back-link">
          ${icon('arrow-left', { size: 'sm' })}<span>${esc(t('common.back'))}</span>
        </a>
        ${this.state.error ? `<div class="error-banner">${esc(this.state.error)}</div>` : ''}
        ${this._headerHtml(r, isReq)}
        ${this._itemsHtml(r, isReq, isTenant)}
        ${isReq && this.state.addItemOpen ? this._addItemFormHtml() : ''}
      </section>
    `;
  }

  _headerHtml(r, isReq) {
    const due = r.due_at ? formatDateTime(String(r.due_at).replace(' ', 'T')) : null;
    const tenant = r.tenant_name || r.tenant_email || '';
    const requester = r.requester_name || r.requester_email || '';
    const canSend = isReq && r.status === 'DRAFT';
    const canCancel = isReq && r.status !== 'APPROVED' && r.status !== 'CANCELLED';
    return `
      <div class="card stack">
        <div class="row-between">
          <h2>${esc(r.title)}</h2>
          <span class="badge">${esc(r.status)}</span>
        </div>
        <div class="muted">
          ${esc(t('tenant_requests.detail.requester'))}<strong>${esc(requester)}</strong>
          · ${esc(t('tenant_requests.detail.tenant'))}<strong>${esc(tenant)}</strong>
          ${due ? ' · ' + esc(t('tenant_requests.row.due', { date: due })) : ''}
        </div>
        ${r.note ? `<div class="faint">${esc(r.note)}</div>` : ''}
        ${(canSend || canCancel) ? `
          <div class="row" style="gap:var(--space-2); flex-wrap:wrap">
            ${canSend ? `<button class="btn btn-primary" data-action="send" ${this.state.busy ? 'disabled' : ''}>
              ${icon('send', { size: 'sm' })}<span>${esc(t('tenant_requests.detail.send'))}</span></button>` : ''}
            ${canCancel ? `<button class="btn btn-ghost" data-action="cancel-request">
              ${icon('x', { size: 'sm' })}<span>${esc(t('tenant_requests.detail.cancel'))}</span></button>` : ''}
          </div>` : ''}
      </div>
    `;
  }

  _itemsHtml(r, isReq, isTenant) {
    const items = r.items || [];
    return `
      <div class="card stack">
        <div class="row-between">
          <h3>${esc(t('tenant_requests.detail.items_title'))}</h3>
          ${isReq && r.status !== 'APPROVED' && r.status !== 'CANCELLED'
            ? `<button class="btn btn-ghost" data-action="add-item">
                ${icon('plus', { size: 'sm' })}<span>${esc(t('tenant_requests.detail.add_item'))}</span></button>`
            : ''}
        </div>
        ${items.length === 0
          ? `<div class="empty">${esc(t('tenant_requests.detail.no_items'))}</div>`
          : `<div class="list">${items.map((it) => this._itemRowHtml(r, it, isReq, isTenant)).join('')}</div>`}
      </div>
    `;
  }

  _itemRowHtml(r, it, isReq, isTenant) {
    const file = it.current_file;
    const reqOpen = r.status !== 'DRAFT' && r.status !== 'APPROVED' && r.status !== 'CANCELLED';
    const canUpload = isTenant && reqOpen && it.status !== 'APPROVED';
    const canReview = isReq && it.status === 'SUBMITTED';
    const canSkip = isReq && Number(it.is_required) === 0 && it.status !== 'APPROVED' && it.status !== 'SKIPPED';
    const canRemove = isReq && r.status === 'DRAFT';
    return `
      <div class="list-item" style="align-items:flex-start">
        ${icon('file-text', { class: 'list-leading icon-faint' })}
        <div class="stack-2 grow">
          <strong>${esc(it.label || it.kind)} ${Number(it.is_required) === 1 ? '<span class="faint">*</span>' : ''}</strong>
          ${it.description ? `<span class="faint">${esc(it.description)}</span>` : ''}
          ${file ? `
            <span class="faint">
              ${esc(t('tenant_requests.detail.file'))}
              <a href="#" data-action="download" data-item-id="${esc(it.id)}">${esc(file.original_name)}</a>
              · ${esc(file.mime)}
            </span>` : ''}
          ${it.status === 'REJECTED' && it.rejection_reason
            ? `<span class="error">${esc(t('tenant_requests.detail.rejected'))}${esc(it.rejection_reason)}</span>` : ''}
          ${canUpload ? this._uploadFormHtml(it) : ''}
          ${canReview && this.state.rejectOpenItemId === it.id ? this._rejectFormHtml(it) : ''}
        </div>
        <div class="stack-2" style="align-items:flex-end">
          <span class="badge">${esc(it.status)}</span>
          ${canReview ? `
            <div class="row" style="gap:var(--space-2)">
              <button class="btn btn-primary btn-sm" data-action="approve" data-item-id="${esc(it.id)}">${esc(t('tenant_requests.detail.approve'))}</button>
              <button class="btn btn-ghost btn-sm" data-action="open-reject" data-item-id="${esc(it.id)}">${esc(t('tenant_requests.detail.reject'))}</button>
            </div>` : ''}
          ${canSkip ? `<button class="btn btn-ghost btn-sm" data-action="skip" data-item-id="${esc(it.id)}">${esc(t('tenant_requests.detail.skip'))}</button>` : ''}
          ${canRemove ? `<button class="btn btn-ghost btn-icon" data-action="remove-item" data-item-id="${esc(it.id)}" aria-label="${esc(t('common.delete'))}">${icon('trash', { size: 'sm' })}</button>` : ''}
        </div>
      </div>
    `;
  }

  _uploadFormHtml(it) {
    return `
      <form class="row" style="gap:var(--space-2); margin-top:var(--space-2)" data-upload-form data-item-id="${esc(it.id)}">
        <input type="file" name="file" accept="image/*,application/pdf" required />
        <button type="submit" class="btn btn-primary btn-sm" ${this.state.busy ? 'disabled' : ''}>${esc(t('tenant_requests.detail.upload'))}</button>
      </form>
    `;
  }

  _rejectFormHtml(it) {
    return `
      <form class="stack-2" data-reject-form data-item-id="${esc(it.id)}" style="margin-top:var(--space-2)">
        <input class="input" name="reason" required maxlength="255" placeholder="${esc(t('tenant_requests.detail.reject_reason'))}" value="${esc(this.state.rejectReason)}" />
        <div class="row" style="gap:var(--space-2); justify-content:flex-end">
          <button type="button" class="btn btn-ghost btn-sm" data-action="close-reject">${esc(t('common.cancel'))}</button>
          <button type="submit" class="btn btn-primary btn-sm">${esc(t('tenant_requests.detail.reject_confirm'))}</button>
        </div>
      </form>
    `;
  }

  _addItemFormHtml() {
    return `
      <form class="card stack" data-add-item-form>
        <h3>${esc(t('tenant_requests.detail.add_item_title'))}</h3>
        <div class="row" style="gap:var(--space-3)">
          <div class="field grow">
            <label>${esc(t('tenant_requests.detail.item_kind'))}</label>
            <select class="select" name="kind">
              ${ITEM_KINDS.map((k) => `<option value="${k}">${k}</option>`).join('')}
            </select>
          </div>
          <div class="field grow">
            <label>${esc(t('tenant_requests.detail.item_label'))}</label>
            <input class="input" name="label" maxlength="190" />
          </div>
        </div>
        <div class="field">
          <label>${esc(t('tenant_requests.detail.item_description'))}</label>
          <input class="input" name="description" maxlength="255" />
        </div>
        <label class="row" style="gap:var(--space-3); align-items:center">
          <input type="checkbox" name="is_required" checked />
          <span>${esc(t('tenant_requests.detail.item_required'))}</span>
        </label>
        <div class="row" style="justify-content:flex-end; gap:var(--space-3)">
          <button type="button" class="btn btn-ghost" data-action="cancel-add-item">${esc(t('common.cancel'))}</button>
          <button type="submit" class="btn btn-primary" ${this.state.busy ? 'disabled' : ''}>${esc(t('common.save'))}</button>
        </div>
      </form>
    `;
  }

  mount() {
    this.on('[data-action="add-item"]', 'click', () => this.setState({ addItemOpen: true, error: null }));
    this.on('[data-action="cancel-add-item"]', 'click', () => this.setState({ addItemOpen: false, error: null }));
    this.on('[data-action="send"]', 'click', () => this._sendDraft());
    this.on('[data-action="cancel-request"]', 'click', () => this._cancelRequest());
    this.on('[data-action="approve"]', 'click', (e) => this._review(e.currentTarget.getAttribute('data-item-id'), 'APPROVED'));
    this.on('[data-action="skip"]', 'click', (e) => this._review(e.currentTarget.getAttribute('data-item-id'), 'SKIPPED'));
    this.on('[data-action="open-reject"]', 'click', (e) => this.setState({
      rejectOpenItemId: e.currentTarget.getAttribute('data-item-id'), rejectReason: '', error: null,
    }));
    this.on('[data-action="close-reject"]', 'click', () => this.setState({ rejectOpenItemId: null, rejectReason: '' }));
    this.on('[data-action="remove-item"]', 'click', (e) => this._removeItem(e.currentTarget.getAttribute('data-item-id')));
    this.on('[data-action="download"]', 'click', (e) => { e.preventDefault(); this._download(e.currentTarget.getAttribute('data-item-id')); });
    this.el.querySelectorAll('[data-upload-form]').forEach((f) => f.addEventListener('submit', (e) => this._upload(e)));
    this.el.querySelectorAll('[data-reject-form]').forEach((f) => f.addEventListener('submit', (e) => this._submitReject(e)));
    const addForm = this.el.querySelector('[data-add-item-form]');
    if (addForm) addForm.addEventListener('submit', (e) => this._addItem(e));
    if (this.state.request === null) this._load();
  }

  async _load() {
    try {
      const r = await api.get(`/tenant-requests/${encodeURIComponent(this.id)}`);
      this.setState({ request: r, error: null, busy: false });
    } catch (err) {
      this.setState({ error: err instanceof ApiError ? err.message : t('tenant_requests.errors.load') });
    }
  }

  async _sendDraft() {
    this.setState({ busy: true });
    try { await api.post(`/tenant-requests/${this.id}/send`); this._load(); }
    catch (err) { this.setState({ busy: false, error: err instanceof ApiError ? err.message : t('tenant_requests.errors.send') }); }
  }

  async _cancelRequest() {
    if (!confirm(t('tenant_requests.detail.cancel_confirm'))) return;
    try { await api.post(`/tenant-requests/${this.id}/cancel`); this._load(); }
    catch (err) { this.setState({ error: err instanceof ApiError ? err.message : t('tenant_requests.errors.cancel') }); }
  }

  async _addItem(e) {
    e.preventDefault();
    const fd = new FormData(e.target);
    const payload = {
      kind: String(fd.get('kind') || ''),
      label: String(fd.get('label') || '').trim() || null,
      description: String(fd.get('description') || '').trim() || null,
      is_required: !!e.target.querySelector('input[name="is_required"]').checked,
    };
    this.setState({ busy: true });
    try {
      await api.post(`/tenant-requests/${this.id}/items`, payload);
      this.setState({ addItemOpen: false, busy: false });
      this._load();
    } catch (err) { this.setState({ busy: false, error: err instanceof ApiError ? err.message : t('tenant_requests.errors.add_item') }); }
  }

  async _removeItem(itemId) {
    if (!confirm(t('tenant_requests.detail.remove_item_confirm'))) return;
    try { await api.delete(`/tenant-requests/${this.id}/items/${itemId}`); this._load(); }
    catch (err) { this.setState({ error: err instanceof ApiError ? err.message : t('tenant_requests.errors.remove_item') }); }
  }

  async _upload(e) {
    e.preventDefault();
    const itemId = e.target.getAttribute('data-item-id');
    const fd = new FormData(e.target);
    this.setState({ busy: true });
    try {
      await api.post(`/tenant-requests/${this.id}/items/${itemId}/upload`, fd);
      this._load();
    } catch (err) { this.setState({ busy: false, error: err instanceof ApiError ? err.message : t('tenant_requests.errors.upload') }); }
  }

  async _review(itemId, decision, rejectionReason = null) {
    try {
      await api.post(`/tenant-requests/${this.id}/items/${itemId}/review`, {
        decision, rejection_reason: rejectionReason,
      });
      this.setState({ rejectOpenItemId: null, rejectReason: '' });
      this._load();
    } catch (err) { this.setState({ error: err instanceof ApiError ? err.message : t('tenant_requests.errors.review') }); }
  }

  async _submitReject(e) {
    e.preventDefault();
    const itemId = e.target.getAttribute('data-item-id');
    const reason = String(new FormData(e.target).get('reason') || '').trim();
    if (!reason) return;
    this._review(itemId, 'REJECTED', reason);
  }

  async _download(itemId) {
    try {
      const blob = await api.get(`/tenant-requests/${this.id}/items/${itemId}/file`, { responseType: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = '';
      document.body.appendChild(a); a.click();
      setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 0);
    } catch (err) { this.setState({ error: err instanceof ApiError ? err.message : t('tenant_requests.errors.download') }); }
  }
}
